import React from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, Trophy, Zap, Layers } from 'lucide-react';
import clsx from 'clsx';
import { TrackId } from '../engine/types';

type TrackOption = {
  id: TrackId;
  tag: string;
  title: string;
  identity: string;
  goal: string;
  promise: string;
  buttonLabel: string;
  image: string;
  accent: 'emerald' | 'violet' | 'blue';
  icon: React.ReactNode;
};

const TRACKS: TrackOption[] = [
  { 
    id: 'creator', 
    tag: 'Greenfield Advantage', 
    title: 'The Creator', 
    identity: "I am launching a new venture.",
    goal: "I want to skip the 'Legacy Phase' and build an AI-Native organization from Day 1.",
    promise: "Scale to Series A output with a Seed stage headcount.",
    buttonLabel: "Run Startup Diagnostic",
    // UPDATE: New Image Path
    image: '/images/illustrations/card_creator.avif', 
    accent: 'emerald',
    icon: <Trophy size={14} />
  },
  { 
    id: 'transformer', 
    tag: 'Operational Velocity', 
    title: 'The Transformer', 
    identity: "I am optimizing an existing company.",
    goal: "My data is siloed, documentation stale, and team drowning in manual process debt.",
    promise: "Map your friction points and automate the high-skill toil.",
    buttonLabel: "Run Operations Diagnostic",
    // UPDATE: New Image Path
    image: '/images/illustrations/card_transformer.avif', 
    accent: 'violet',
    icon: <Zap size={14} />
  },
  { 
    id: 'builder', 
    tag: 'Product Architecture', 
    title: 'The Builder', 
    identity: "I am building a product.",
    goal: "I want to move beyond 'Chatbot Wrappers' and build deep, agentic workflows.",
    promise: "Inject a 'Thinking Core' into your product infrastructure.",
    buttonLabel: "Run Product Diagnostic",
    // UPDATE: New Image Path
    image: '/images/illustrations/card_builder.avif', 
    accent: 'blue',
    icon: <Layers size={14} />
  },
];

// Color Logic for the "Sovereign" Theme
const THEME = {
  emerald: {
    borderHover: "group-hover:border-emerald-500/50",
    glow: "group-hover:shadow-[0_0_30px_-5px_rgba(16,185,129,0.3)]",
    badge: "text-emerald-400 border-emerald-500/30 bg-emerald-500/10",
    titleHover: "group-hover:text-emerald-400",
    promise: "text-emerald-400",
    button: "group-hover:bg-emerald-500 group-hover:text-slate-950 group-hover:border-emerald-500"
  },
  violet: {
    borderHover: "group-hover:border-violet-500/50",
    glow: "group-hover:shadow-[0_0_30px_-5px_rgba(139,92,246,0.3)]",
    badge: "text-violet-400 border-violet-500/30 bg-violet-500/10",
    titleHover: "group-hover:text-violet-400",
    promise: "text-violet-400",
    button: "group-hover:bg-violet-500 group-hover:text-slate-950 group-hover:border-violet-500"
  },
  blue: {
    borderHover: "group-hover:border-blue-500/50",
    glow: "group-hover:shadow-[0_0_30px_-5px_rgba(59,130,246,0.3)]",
    badge: "text-blue-400 border-blue-500/30 bg-blue-500/10",
    titleHover: "group-hover:text-blue-400",
    promise: "text-blue-400",
    button: "group-hover:bg-blue-500 group-hover:text-slate-950 group-hover:border-blue-500"
  }
};

export const TrackSelector: React.FC<{ onSelect: (id: TrackId) => void }> = ({ onSelect }) => {
  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-8 items-stretch">
      {TRACKS.map((track, idx) => {
        const theme = THEME[track.accent];

        return (
          <motion.div
            key={track.id}
            onClick={() => onSelect(track.id)}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: idx * 0.1 }}
            className={clsx(
              "group cursor-pointer relative flex flex-col h-full",
              "bg-slate-900 rounded-xl overflow-hidden",
              "border border-slate-800 transition-all duration-300",
              theme.borderHover, // Dynamic Border Color
              theme.glow // Dynamic Neon Glow
            )}
          >
            {/* Image Header */}
            <div className="h-48 w-full bg-slate-950 relative overflow-hidden border-b border-slate-800">
               <div className="absolute inset-0 bg-slate-900/50 z-10 transition-opacity duration-300 group-hover:opacity-0" />
               <img 
                 src={track.image} 
                 alt={track.title}
                 className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110 opacity-90 group-hover:opacity-100" 
                 onError={(e) => { e.currentTarget.style.display = 'none'; }}
               />
               
               {/* Badge */}
               <div className="absolute top-4 left-4 z-20">
                  <span className={clsx(
                    "text-[10px] font-black uppercase tracking-widest px-3 py-1.5 rounded-md backdrop-blur-md shadow-lg border flex items-center gap-1.5",
                    theme.badge
                  )}>
                     {track.icon} {track.tag}
                  </span>
               </div>
            </div>

            {/* Content Body */}
            <div className="p-6 md:p-8 flex-grow flex flex-col relative z-20">
               {/* Subtle Grid Background in Body */}
               <div className="absolute inset-0 opacity-5 bg-[radial-gradient(#fff_1px,transparent_1px)] [background-size:16px_16px] pointer-events-none" />

               <div className="mb-6 relative">
                   <h3 className={clsx(
                     "text-2xl font-black text-white mb-2 leading-tight transition-colors duration-300",
                     theme.titleHover
                   )}>
                      {track.title}
                   </h3>
                   <p className="text-sm font-medium text-slate-400 italic">"{track.identity}"</p>
               </div>
               
               <div className="space-y-5 flex-grow mb-8 relative">
                  <div>
                      <h4 className="text-[10px] font-black uppercase tracking-widest text-slate-500 mb-1">The Goal</h4>
                      <p className="text-sm font-medium text-slate-300 leading-relaxed">{track.goal}</p>
                  </div>
                  <div>
                      <h4 className={clsx("text-[10px] font-black uppercase tracking-widest mb-1 opacity-80", theme.promise)}>The Promise</h4>
                      <p className={clsx("text-sm font-bold leading-relaxed", theme.promise)}>{track.promise}</p>
                  </div>
               </div>
               
               {/* CTA Button */}
               <div className={clsx(
                 "w-full py-3 px-4 rounded-lg border border-slate-700 font-bold text-sm text-center flex items-center justify-center gap-2 transition-all duration-300",
                 "bg-slate-800 text-slate-300 mt-auto relative z-10",
                 theme.button
               )}>
                  <span className="block group-hover:hidden">{track.buttonLabel}</span>
                  <span className="hidden group-hover:block">Start Diagnostic</span>
                  <ArrowRight size={14} />
               </div>
            </div>
          </motion.div>
        );
      })}
    </div>
  );
};